import { useCallback, useMemo, useRef, useState } from 'react';

import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AxiosError } from 'axios';
import { usePostHog } from 'posthog-js/react';

import { prepareCsvWidgetExport } from '@/components/CsvTableWidget/CsvWidgetExport/downloadCsvWidgetExport';
import { useCsvWidgetExportMutation } from '@/components/CsvTableWidget/CsvWidgetExport/useCsvWidgetExportMutation';
import Typography from '@/components/Typography';
import { Permission } from '@/entities/permissions';
import { pastExportsOptions, useDeletePastExport } from '@/features/DataHub/api';
import { mapExportItemToDetails } from '@/features/DataHub/api/pastExports.api';
import { DataHubHeader } from '@/features/DataHub/components/DataHubHeader';
import { DATAHUB_TITLES } from '@/features/DataHub/constants/titles';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { optionById } from '@/features/DataHub/modules/Export/constants/exportOptions';
import { useExportRequest } from '@/features/DataHub/modules/Export/context/exportContexts';
import { useExportApiKey } from '@/features/DataHub/modules/Export/hooks/useExportApiKey';
import { useLatestApiKey } from '@/features/DataHub/modules/Export/hooks/useLatestApiKey';
import {
  CLIENT_SIDE_DOWNLOAD_TYPES,
  openGxApiUrl,
  performClientSideDownload
} from '@/features/DataHub/modules/Export/services/ClientSideDownloadService';
import {
  ANALYTICS_EVENTS,
  buildCsvWidgetExportEventData,
  buildPastExportEventData
} from '@/features/DataHub/modules/Export/services/ExportAnalyticsService';
import BasicUserView from '@/features/DataHub/pages/PastExports/components/BasicUserView';
import {
  type DerivedExport,
  ExportItem
} from '@/features/DataHub/pages/PastExports/components/ExportItem';
import { ExportItemsSkeleton } from '@/features/DataHub/pages/PastExports/components/ExportItemsSkeleton';
import { PastExportsEmptyState } from '@/features/DataHub/pages/PastExports/components/PastExportsEmptyState';
import { isSourceBackedDataExport } from '@/features/DataHub/utils/isSourceBackedDataExport';
import {
  buildSourceBackedDataExportAdditionalParams,
  hasSourceBackedDataExportDateFilter,
  normalizeSourceBackedDataExportFrom
} from '@/features/DataHub/utils/sourceBackedDataExport';
import useDocumentTitleRename from '@/hooks/useDocumentTitleRename';
import usePermission from '@/shared/hooks/usePermission';
import { triggerBlobDownload } from '@/utils/triggerBlobDownload';

export const PastExports = () => {
  useDocumentTitleRename(DATAHUB_TITLES.pastExports);
  const { data, isError, isLoading, error } = useQuery(pastExportsOptions.list());
  const isEmpty = data?.length === 0;
  const { hasPermission } = usePermission();
  const canViewPastExports = hasPermission(Permission.ViewPastExports);

  const sortedData = useMemo(
    () => data?.toSorted((a, b) => b.lastExecutionTime - a.lastExecutionTime),
    [data]
  );

  const posthog = usePostHog();
  const { exportDataWithParams, setExportStatus } = useExportRequest();
  const { mutate: deleteMutate } = useDeletePastExport();
  const { timezoneValue, dateFormatValue } = useDataHubUserConfig();
  const apiKey = useExportApiKey();
  const latestApiKey = useLatestApiKey();
  const { mutateAsync: csvWidgetExportMutateAsync } = useCsvWidgetExportMutation();

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const handleToggleExpanded = useCallback((id: string, expanded: boolean) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (expanded) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const derivedExports = useMemo<DerivedExport[]>(() => {
    if (!sortedData) return [];
    return sortedData.map(item => {
      const details = mapExportItemToDetails(item);
      const isSourceBackedDataItem = isSourceBackedDataExport(item);
      const resolvedSourceSettings = details.sourceSettings ?? item.sourceSettings;
      const effectiveType = details.type === 'third-party' ? details.exportSubType : details.type;
      const option = effectiveType ? (optionById.get(effectiveType) ?? null) : null;
      const optionLabel = option?.label || 'N/A';
      const isOngoing = item.exportStatus === 'ongoing';
      const isMissingToken = Boolean(option?.showTokenLink) && !latestApiKey;
      const canRegenerate =
        (item.exportStatus === 'finished' || item.exportStatus === 'failed') &&
        canViewPastExports &&
        !isMissingToken &&
        (!isSourceBackedDataItem || Boolean(resolvedSourceSettings));
      return {
        item,
        details,
        isSourceBackedDataItem,
        resolvedSourceSettings,
        optionLabel,
        isMissingToken,
        canDelete: !isOngoing,
        canRegenerate
      };
    });
  }, [sortedData, latestApiKey, canViewPastExports]);

  const handleDelete = useCallback(
    (derived: DerivedExport) => {
      const { item } = derived;
      setDeletingId(item.id);
      const { eventName, data: eventData } = buildPastExportEventData(
        ANALYTICS_EVENTS.PAST_EXPORT_DELETED,
        item
      );
      deleteMutate(item.id, {
        onSuccess: () => posthog.capture(eventName, eventData),
        onSettled: () => {
          setDeletingId(null);
        }
      });
    },
    [deleteMutate, posthog]
  );

  const handleSourceBackedRegenerate = useCallback(
    async (derived: DerivedExport) => {
      const { item, resolvedSourceSettings } = derived;
      if (!resolvedSourceSettings) return;

      const { source, from, to, tabName } = resolvedSourceSettings;
      const normalizedFrom = normalizeSourceBackedDataExportFrom(from, to);

      try {
        const preparedExport = await prepareCsvWidgetExport({
          source,
          from: normalizedFrom ?? undefined,
          to: to ?? undefined,
          dateColumnKey: null,
          exportName: item.name,
          dateFormattingConfig: {
            dateFormat: dateFormatValue,
            timezone: timezoneValue
          }
        });

        await csvWidgetExportMutateAsync({
          id: item.id,
          name: item.name,
          exportType: 'csv',
          source,
          from: normalizedFrom ?? undefined,
          to: to ?? undefined,
          tabName,
          additionalParams: buildSourceBackedDataExportAdditionalParams(source)
        });

        triggerBlobDownload(preparedExport.blob, preparedExport.fileName);

        const { eventName, data: eventData } = buildCsvWidgetExportEventData(
          ANALYTICS_EVENTS.CSV_WIDGET_EXPORT_REGENERATED,
          {
            exportType: 'trade-csv',
            sourceType: source.sourceType,
            hasDateFilter: hasSourceBackedDataExportDateFilter(normalizedFrom, to)
          }
        );
        posthog.capture(eventName, eventData);
      } catch {
        // error snackbar is handled by useCsvWidgetExportMutation onError
      }
    },
    [csvWidgetExportMutateAsync, dateFormatValue, posthog, timezoneValue]
  );

  const handleRegenerate = useCallback(
    async (derived: DerivedExport) => {
      const { item, details, isSourceBackedDataItem, resolvedSourceSettings } = derived;

      if (isSourceBackedDataItem && resolvedSourceSettings) {
        await handleSourceBackedRegenerate(derived);
        return;
      }

      setExportStatus('loading');

      const effectiveType = details.type === 'third-party' ? details.exportSubType : details.type;
      const isClientSideDownload = effectiveType
        ? effectiveType !== 'gx_api' && CLIENT_SIDE_DOWNLOAD_TYPES.has(effectiveType)
        : false;

      // For gx_api, open the URL synchronously before any await — popup blockers
      // reject window.open calls that happen after an async operation
      if (effectiveType === 'gx_api') {
        openGxApiUrl(details, apiKey);
      }

      try {
        await exportDataWithParams({
          id: item.id,
          name: details.name,
          type: details.type,
          exportSubType: details.exportSubType,
          from: details.from,
          to: details.to,
          includeMetadata: details.metadata === 'true',
          timezone: details.timezone,
          dateFormat: details.dateFormat,
          symbols: details.symbols,
          additionalParams: details.additionalParams
        });

        if (isClientSideDownload && effectiveType) {
          await performClientSideDownload(effectiveType, details, apiKey);
          setExportStatus('idle');
        }

        const { eventName, data: eventData } = buildPastExportEventData(
          ANALYTICS_EVENTS.PAST_EXPORT_REGENERATED,
          item
        );
        posthog.capture(eventName, eventData);
      } catch {
        setExportStatus('idle');
      }
    },
    [apiKey, exportDataWithParams, handleSourceBackedRegenerate, posthog, setExportStatus]
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: derivedExports.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 750,
    overscan: 3
  });

  if (!canViewPastExports) {
    return <BasicUserView />;
  }
  if (isEmpty || (error instanceof AxiosError && error.status === 404))
    return <PastExportsEmptyState />;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        flex: 1,
        p: '24px',
        minHeight: 0
      }}
    >
      <DataHubHeader
        subtitle='Your past exports with details'
        title='Past Exports'
      />
      {isLoading && <ExportItemsSkeleton />}
      {isError && <Typography>Error loading data</Typography>}
      <Box
        ref={scrollRef}
        sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: virtualizer.getTotalSize()
          }}
        >
          {virtualizer.getVirtualItems().map(item => {
            const derived = derivedExports[item.index];
            return (
              <Box
                key={item.key}
                data-index={item.index}
                ref={virtualizer.measureElement}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  pb: '24px',
                  transform: `translateY(${item.start}px)`
                }}
              >
                <ExportItem
                  derived={derived}
                  expanded={expandedIds.has(derived.item.id)}
                  isDeleting={deletingId === derived.item.id}
                  onDelete={handleDelete}
                  onRegenerate={handleRegenerate}
                  onToggleExpanded={handleToggleExpanded}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
